import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n'; // initialise i18next before rendering
import App from './App';
import { ApiError } from './api/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './utils/outlookUtils';
import ErrorBoundary from './components/shared/ErrorBoundary';

// Deploy breadcrumb — the running build's git SHA. Doubles as the live-verify
// signal for the SW force-reload: after an auto-reload this flips to the new SHA.
console.log('Aidey build:', process.env.REACT_APP_BUILD_SHA || 'dev');

// Safety-net: any write call that throws ApiError without a local .catch will
// land here. Shows a toast so the user sees the error instead of a silent stale UI.
window.addEventListener('unhandledrejection', (event) => {
  const err = event.reason;
  if (!(err instanceof ApiError) || err.isAuthError) return;
  const msg = err.body?.error || err.body?.message || `Request failed (${err.status})`;
  const el = document.createElement('div');
  el.className = 'toast-banner';
  el.style.cssText = 'background:#c0392b;color:#fff;';
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 5000);
});

if ('serviceWorker' in navigator) {
  // Single update mechanism: the SW force-reloads controlled tabs on activate
  // (see public/sw.js). This message is the fallback for browsers where SW-side
  // client.navigate() is unreliable (notably Safari). A guard prevents a double
  // reload if navigate already fired.
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'SW_RELOAD' && !window.__aideySwReloaded) {
      window.__aideySwReloaded = true;
      window.location.reload();
    }
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => {
        console.log('Aidey SW registered:', reg.scope);

        // Force update checks so a deploy is discovered — and thus force-reloaded
        // — on long-lived tabs / installed PWAs (esp. Safari, which rarely
        // re-checks sw.js on its own) within one focus, no manual clear.
        const check = () => reg.update().catch(() => {});
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
        window.addEventListener('focus', check);
        setInterval(check, 30 * 60 * 1000); // every 30 minutes
      })
      .catch(err => console.log('SW registration failed:', err));
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Google OAuth Provider setup
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || null;

root.render(
  <ErrorBoundary>
    {/* React.StrictMode disabled to prevent double mounting in development */}
    {googleClientId ? (
      <GoogleOAuthProvider clientId={googleClientId}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </GoogleOAuthProvider>
    ) : (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Google Calendar Integration Not Configured</h2>
        <p>Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file</p>
      </div>
    )}
  </ErrorBoundary>
);
